/*
 * diamond-spin-viewer.js
 *
 * Vanilla-JS 360° spin viewer for diamond product pages.
 * Reads its config from data-attributes on `<div data-spin-viewer ...>` and
 * supports:
 *   - drag with mouse / touch to rotate
 *   - keyboard arrows (left/right step ±1 frame; home → top frame)
 *   - autoplay (continuous rotation after short idle)
 *   - lazy frame preloading (chunked, doesn't block first paint)
 *
 * Frame URLs are constructed as:  {base_url}/{frame_index}.jpg
 * (Frame index runs 0..frame_count-1.)
 *
 * Public DOM contract — see diamond-media-viewer.liquid section for the
 * markup it expects:
 *   data-spin-viewer        : flag attribute, no value
 *   data-base-url           : Nivoda v360_url (no trailing slash)
 *   data-frame-count        : integer (e.g. 256)
 *   data-top-index          : string integer (frame index for the "top face")
 *   data-autoplay           : "true" | "false"
 *
 * Child elements:
 *   .dmv__spin-img          : main img element (we swap its src)
 *   [data-spin-action=play|pause|reset]
 *   [data-spin-progress]    : progress bar fill element
 */
(function () {
  'use strict';

  const ROTATE_PIXELS_PER_FRAME = 6;     // drag sensitivity
  const AUTOPLAY_FPS = 18;
  const PRELOAD_BATCH_SIZE = 16;
  const PRELOAD_BATCH_DELAY_MS = 200;
  const AUTOPLAY_IDLE_DELAY_MS = 2500;

  function init(root) {
    const baseUrl = root.dataset.baseUrl;
    const frameCount = parseInt(root.dataset.frameCount || '0', 10);
    const topIndex = parseInt(root.dataset.topIndex || '0', 10);
    const autoplayConfig = root.dataset.autoplay === 'true';

    if (!baseUrl || frameCount <= 0) return;

    const img = root.querySelector('.dmv__spin-img');
    const progressBar = root.querySelector('[data-spin-progress]');
    const btnPlay = root.querySelector('[data-spin-action=play]');
    const btnPause = root.querySelector('[data-spin-action=pause]');
    const btnReset = root.querySelector('[data-spin-action=reset]');
    if (!img) return;

    let currentFrame = topIndex;
    let autoplayTimer = null;
    let lastInteractionAt = Date.now();
    let dragStartX = null;
    let dragStartFrame = null;
    const preloaded = new Set();

    function frameUrl(i) {
      return baseUrl + '/' + i + '.jpg';
    }

    function setFrame(i) {
      const safe = ((i % frameCount) + frameCount) % frameCount;
      currentFrame = safe;
      img.src = frameUrl(safe);
      if (progressBar) {
        progressBar.style.width = ((safe / (frameCount - 1)) * 100).toFixed(1) + '%';
      }
    }

    function preloadFrame(i) {
      if (preloaded.has(i)) return;
      const p = new Image();
      p.src = frameUrl(i);
      preloaded.add(i);
    }

    /* Lazy preload all frames in chunks so the spin is smooth once active
       but the page doesn't pay the bandwidth cost up-front. */
    function preloadAll() {
      let idx = 0;
      function pump() {
        const end = Math.min(idx + PRELOAD_BATCH_SIZE, frameCount);
        for (; idx < end; idx++) preloadFrame(idx);
        if (idx < frameCount) setTimeout(pump, PRELOAD_BATCH_DELAY_MS);
      }
      // Start one batch immediately, then schedule the rest.
      pump();
    }

    /* Drag handling — mouse + touch, unified via pointer events. */
    function onPointerDown(ev) {
      stopAutoplay();
      lastInteractionAt = Date.now();
      dragStartX = ev.clientX !== undefined ? ev.clientX : ev.touches[0].clientX;
      dragStartFrame = currentFrame;
      root.classList.add('dmv__spin--dragging');
      root.setPointerCapture && root.setPointerCapture(ev.pointerId || 0);
    }
    function onPointerMove(ev) {
      if (dragStartX === null) return;
      const x = ev.clientX !== undefined ? ev.clientX : ev.touches && ev.touches[0].clientX;
      if (x === undefined) return;
      const delta = x - dragStartX;
      const framesDelta = Math.round(delta / ROTATE_PIXELS_PER_FRAME);
      setFrame(dragStartFrame + framesDelta);
      ev.preventDefault();
    }
    function onPointerUp() {
      dragStartX = null;
      dragStartFrame = null;
      root.classList.remove('dmv__spin--dragging');
      lastInteractionAt = Date.now();
    }

    /* Keyboard accessibility. */
    function onKey(ev) {
      if (ev.key === 'ArrowLeft') { setFrame(currentFrame - 1); ev.preventDefault(); }
      else if (ev.key === 'ArrowRight') { setFrame(currentFrame + 1); ev.preventDefault(); }
      else if (ev.key === 'Home') { setFrame(topIndex); ev.preventDefault(); }
    }

    /* Autoplay (continuous rotation). Pauses while user drags or hovers controls. */
    function startAutoplay() {
      if (autoplayTimer) return;
      btnPlay && (btnPlay.hidden = true);
      btnPause && (btnPause.hidden = false);
      autoplayTimer = setInterval(() => setFrame(currentFrame + 1), Math.round(1000 / AUTOPLAY_FPS));
    }
    function stopAutoplay() {
      if (!autoplayTimer) return;
      clearInterval(autoplayTimer);
      autoplayTimer = null;
      btnPlay && (btnPlay.hidden = false);
      btnPause && (btnPause.hidden = true);
    }

    /* Optional idle autoplay — kicks in after AUTOPLAY_IDLE_DELAY_MS of no interaction. */
    function maybeIdleAutoplay() {
      if (!autoplayConfig || !activated) return;
      const elapsed = Date.now() - lastInteractionAt;
      if (elapsed >= AUTOPLAY_IDLE_DELAY_MS && !autoplayTimer) startAutoplay();
    }
    if (autoplayConfig) setInterval(maybeIdleAutoplay, 1000);

    /* Wire events. */
    root.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    root.tabIndex = 0;
    root.addEventListener('keydown', onKey);
    btnPlay && btnPlay.addEventListener('click', startAutoplay);
    btnPause && btnPause.addEventListener('click', stopAutoplay);
    btnReset && btnReset.addEventListener('click', () => { stopAutoplay(); setFrame(topIndex); });

    /* Activate — but only download the frame set once the spin pane is actually
       shown. Preloading N frames (and idle-autoplaying through them) while the
       pane is hidden cost bandwidth on every product view for nothing. */
    setFrame(topIndex);
    let activated = false;
    function activate() {
      if (activated) return;
      activated = true;
      lastInteractionAt = Date.now();
      preloadAll();
    }
    const pane = root.closest('[role=tabpanel]');
    const dmv = root.closest('.dmv');
    if (!pane || !pane.hasAttribute('hidden')) activate();
    if (dmv) dmv.addEventListener('dmv:view', (e) => { if (e.detail && e.detail.view === 'spin') activate(); });
  }

  /* Tab switching for the parent .dmv container. */
  function initTabs(container) {
    const tabs = container.querySelectorAll('[role=tab]');
    const panes = container.querySelectorAll('[role=tabpanel]');
    const defaultView = container.dataset.defaultView || 'photo';

    function show(view) {
      tabs.forEach((t) => t.setAttribute('aria-selected', t.dataset.view === view ? 'true' : 'false'));
      panes.forEach((p) => {
        const matches = p.dataset.view === view;
        if (matches) {
          p.removeAttribute('hidden');
          p.classList.add('dmv__pane--active');
          // Heavy embeds (Loupe360 video, ~1.5 MB) carry `data-src` and only start
          // loading the first time their pane is opened.
          p.querySelectorAll('iframe[data-src]').forEach((f) => { f.src = f.dataset.src; f.removeAttribute('data-src'); });
        } else {
          p.setAttribute('hidden', '');
          p.classList.remove('dmv__pane--active');
        }
      });
      container.dispatchEvent(new CustomEvent('dmv:view', { detail: { view } }));
    }

    tabs.forEach((t) => t.addEventListener('click', () => show(t.dataset.view)));
    // Overlay button on the photo ("360° video" / "360° spin") opens that tab.
    container.querySelectorAll('[data-dmv-open]').forEach((b) => b.addEventListener('click', () => show(b.dataset.dmvOpen)));
    show(defaultView);
  }

  function boot() {
    document.querySelectorAll('.dmv').forEach(initTabs);
    document.querySelectorAll('[data-spin-viewer]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
